import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { IAxiosDefaultDataObject } from "../../config";
import BaseApp, { IBaseApp, IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional } from "../BaseApp";
import { addNewProject, deleteProject, setProjectsSearchParams, setSelectedProjects, updateAllProjects, updateProject, updateProjectsLoadingStatus, updateProjectsPagination, updateProjectsSearch, useReturnAllProjects, useReturnProjectsLoadingStatus, useReturnProjectsPagination, useReturnProjectsSearch, useReturnProjectsSearchParams, useReturnSelectedProjects } from "./projectsSlice";
import { addNewLocation, useReturnAllLocations } from "../locations/locationsSlice";
import LocationsApp from "../locations/LocationsApp";
import { GridDataColumns, IGridDataConfig, IGridDataEssentials } from "../../components/GridData";
import { ICellRendererParams } from "ag-grid-community";
import { Link } from "react-router-dom";
import ProjectQuickPreview from "./ProjectQuickPreview";

/**
 * Brand Model
 */
export interface IProjectModel {
    id: number,
    title: string,
    details: string,
    start_date: string,
    end_date: string,
    location_id: number,
    thumbnail: string,
    created_by: number,
    created_by_name: string,
    date_created: string,
    last_updated: string,
    is_active: boolean,
    location_title: string,
}

export const EmptyProjectModel: IProjectModel = {
    id: 0,
    title: "",
    details: "",
    start_date: "",
    end_date: "",
    location_id: 0,
    thumbnail: "",
    created_by: 0,
    created_by_name: "",
    date_created: "",
    last_updated: "",
    is_active: false,
    location_title: "",
}

/**
 * Project App
 */
export default class ProjectsApp extends BaseApp implements IBaseApp {

    appName = "projects"
    appBaseEndPoint = "/projects"

    // customListEndPoint: string = this.appBaseEndPoint

    messages = {
        create: "Create a new project",
        createShort: "New project",
        edit: "Edit project",
        editShort: "Edit",
        delete: "Delete project",
        deleteShort: "Delete",
        view: "View project",
        nothingFound: "No projects have been found.",
    }

    elemPrefix: string = "projects_"

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "thumbnail",
                type: "thumbnail",
                label: "Thumbnail"
            },
            {
                id: "title",
                type: "text",
                label: "Title",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "location_title",
                type: "text",
                label: "Location",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "start_date",
                type: "date",
                label: "Start Date",
                maxWidth: 250,
                minWidth: 250,
            },
            {
                id: "end_date",
                type: "date",
                label: "End Date",
                maxWidth: 250,
                minWidth: 250,
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No projects have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "projects_form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 8,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "location_id",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Location",
                    fullWidth: true,
                    appClass: new LocationsApp(),
                    selectLabel: "Select Location",
                    gridColumns: new LocationsApp().gridDataColumns
                },
                {
                    id: this.elemPrefix + "start_date",
                    xs: 6,
                    type: "DateAndTime",
                    label: "Start date",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "end_date",
                    xs: 6,
                    type: "DateAndTime",
                    label: "End date",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "details",
                    xs: 12,
                    type: "TextField",
                    label: "Details",
                    multiline: true,
                    minRows: 4,
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the location details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
            {
                id: this.elemPrefix + "location_id",
                message: "Location is required!"
            },
            {
                id: this.elemPrefix + "start_date",
                message: "Start date is required!"
            },
            {
                id: this.elemPrefix + "end_date",
                message: "End date is required!"
            },
        ]
    }

    /**
     * Quick Preview
     */
    quickPreview = (id: number) => {
        return <ProjectQuickPreview projectId={id} />
    }

    /**
     * Redux
     */
    redux = {
        updateItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(updateProject((item as IProjectModel))),
        addNewItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(addNewProject((item as IProjectModel)))
    }

    /**
     * Dynamic Base Page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllProjects,
        pagination: useReturnProjectsPagination,
        loading: useReturnProjectsLoadingStatus,
        search: useReturnProjectsSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateProjectsLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteProject({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewProject((item as IProjectModel))),
        updateItem: (item, dispatch) => dispatch(updateProject((item as IProjectModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllProjects((items as IProjectModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateProjectsSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateProjectsPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/projects/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/projects/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
        // filterForm: [],
        // customHeaderOptionActionButtons: [],
        // viewItemTemplateLink: "",
        // disableAutoViewItemLink: false,
        // hidePageHeadSection: false,
        // customTitlePageHeadSection: "",
        // disableSearch: false,
        // customPageHeadSectionActionButtons: []
    }
    
    /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        // {
        //     field: "thumbnail",
        //     headerName: "thumbnail",
        //     type: "thumbnail",
        //     resizable: false,
        //     width: 110,
        // },
        // {
        //     field: "title",
        //     headerName: "Title",
        //     cellRenderer: (params: ICellRendererParams<IProjectModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/projects/view/${item.id}`}>
        //                     <h3 className="title">{item.title}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
        {
            field: "title",
            headerName: "Project",
            type: "title",
            width: 350,
            minWidth: 350,
            cellRendererParams: {
                appClass: () => new ProjectsApp(),
                enableQuickPreview: true,
            }
        },
        // {
        //     field: "location_title",
        //     headerName: "Location",
        //     cellRenderer: (params: ICellRendererParams<IProjectModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/locations/view/${item.location_id}`}>
        //                     <h3 className="title">{item.location_title}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
        {
            field: "location_title",
            headerName: "Location",
            type: "quick-preview",
            width: 300,
            minWidth: 300,
            cellRendererParams: {
                appClass: () => new LocationsApp(),
                elemIdTitle: "location_id",
            }
        },
        {
            field: "date_created",
            headerName: "Date Created",
            type: "date",
        },
        {
            field: "start_date",
            headerName: "Start",
            type: "date",
        },
        {
            field: "end_date",
            headerName: "End",
            type: "date",
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/projects/view/[id]",
        editItemLink: "/panel/projects/edit/[id]",
        hideFilters: true,
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedProjects,
        searchParams: useReturnProjectsSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedProjects(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(setProjectsSearchParams(params))
    }
    
}